/**
 * Glossario quant — definizioni 1-frase per i termini tecnici usati nelle
 * pagine /admin/quant/*. Usato da <GlossaryTerm term="Sharpe">Sharpe</GlossaryTerm>
 * che renderizza il testo con un popover sulla definizione.
 *
 * Convenzioni:
 *  - key in lowercase, normalizzata via key.toLowerCase().trim()
 *  - threshold = soglia di promozione da knowledge/rules/_master.md cap. 4
 *  - direction = "higher_better" | "lower_better" | "neutral" (per UI hint)
 */

export const GLOSSARY = {
  sharpe: {
    title: 'Sharpe Ratio',
    short: 'Rendimento per unità di rischio.',
    detail: 'Misura quanto rende la strategia in eccesso al risk-free, normalizzato per la volatilità. Soglia di promozione: ≥ 1.0. SPY storico ≈ 0.64.',
    threshold: 1.0,
    direction: 'higher_better',
  },
  sortino: {
    title: 'Sortino Ratio',
    short: 'Come Sharpe ma penalizza solo la volatilità al ribasso.',
    detail: 'Variante di Sharpe che ignora la volatilità positiva (i guadagni non sono "rischio"). Più accurato per strategie asimmetriche tipo trend-follow.',
    direction: 'higher_better',
  },
  cagr: {
    title: 'CAGR',
    short: 'Tasso di crescita annualizzato.',
    detail: 'Compound Annual Growth Rate: rendimento medio composto annuo. SPY storico ≈ +10%. Compara periodi di durata diversa.',
    direction: 'higher_better',
  },
  calmar: {
    title: 'Calmar Ratio',
    short: 'CAGR / |Max Drawdown|.',
    detail: 'Quanto rende vs quanto ti fa soffrire. Soglia di promozione: ≥ 0.5. Più alto = meglio.',
    threshold: 0.5,
    direction: 'higher_better',
  },
  max_dd: {
    title: 'Max Drawdown',
    short: 'Peggior perdita peak-to-trough.',
    detail: 'Quanto ha perso il portfolio dal massimo storico al minimo successivo. Soglia: ≥ −25% (cioè non scendere mai sotto il −25%).',
    threshold: -0.25,
    direction: 'higher_better',
  },
  win_rate: {
    title: 'Win Rate',
    short: '% di trade chiusi in profitto.',
    detail: 'Da solo non basta: una strategia con 30% win rate ma payoff 3:1 è migliore di 70% con payoff 0.5:1. Va letto con payoff_ratio.',
    direction: 'neutral',
  },
  payoff_ratio: {
    title: 'Payoff Ratio',
    short: 'Vincita media / perdita media.',
    detail: 'Quanto guadagni in media quando vinci, diviso quanto perdi in media quando perdi. >1 = vinci di più di quanto perdi.',
    direction: 'higher_better',
  },
  beta: {
    title: 'Beta vs benchmark',
    short: 'Sensibilità al mercato.',
    detail: 'Beta 1.0 = si muove come SPY. Beta 0 = decorrelato. Soglia satellite: ≤ 0.7 (deve essere abbastanza indipendente dal core).',
    threshold: 0.7,
    direction: 'lower_better',
  },
  n_trades: {
    title: 'N° Trade',
    short: 'Numero totale di trade chiusi.',
    detail: 'Statistical significance richiede ≥ 5 trade. Sopra 200 sospetto curve-fitting (la strategia trada così tanto da adattarsi al rumore).',
    direction: 'neutral',
  },
  // === Tipi di strategia ===
  positional: {
    title: 'Positional',
    short: 'Holding ~1 mese → 6 mesi.',
    detail: 'Strategie a media frequenza che tengono posizioni per settimane/mesi. Tipico: trend-follow con SMA 50/200.',
  },
  swing: {
    title: 'Swing',
    short: 'Holding 1 giorno → 6 settimane.',
    detail: 'Strategie a breve termine che catturano oscillazioni di mercato. Tipico: breakout Donchian, mean-reversion RSI.',
  },
  baseline: {
    title: 'Baseline',
    short: 'Strategia di riferimento.',
    detail: 'Non viene mai promossa, serve solo come benchmark. Esempio: buy-and-hold di SPY.',
  },
  // === Indicatori tecnici ===
  donchian: {
    title: 'Canale Donchian',
    short: 'Massimo e minimo a N sedute.',
    detail: 'Donchian(20) = massimo e minimo delle ultime 20 sedute. Entry = breakout sopra il massimo. Exit = breakdown sotto il minimo. Inventato da Richard Donchian, popolarizzato dai Turtle Trader (1983).',
  },
  rsi: {
    title: 'RSI',
    short: 'Relative Strength Index — oscillatore di momentum.',
    detail: 'Da 0 a 100. RSI < 30 = oversold (mean-revert compra), > 70 = overbought (mean-revert vende). Wilder 1978.',
  },
  sma: {
    title: 'SMA',
    short: 'Simple Moving Average.',
    detail: 'Media aritmetica del prezzo su N sedute. SMA 50/200 = "golden cross" quando SMA 50 supera SMA 200 (buy signal trend-follow).',
  },
  atr: {
    title: 'ATR',
    short: 'Average True Range — volatilità in $ per seduta.',
    detail: 'Quanto si muove in media il prezzo (range high-low + gap) su N sedute. Usato per stop loss adattivi: SL = entry − 2×ATR(14) si adatta alla volatilità del momento.',
  },
  // === Sizing methods ===
  equal_weight: {
    title: 'Equal Weight (fixed)',
    short: 'Alloca % fissa del capitale iniziale per asset.',
    detail: 'Es. 10% × 3 asset = 30% allocato, 70% cash residuo perennemente. Sub-account indipendenti, PnL non rientra nel pool. Conservativo ma sotto-utilizza il capitale.',
  },
  equal_weight_compound: {
    title: 'Equal Weight (compound)',
    short: 'Reinveste PnL chiuso, ribilancia sul current equity.',
    detail: 'Stesso peso % ma ricalcolato a ogni segnale sul valore corrente del portfolio. Niente cash drag fisso. Amplifica edge se c\'è (e amplifica perdite se non c\'è).',
  },
  all_in: {
    title: 'All-In',
    short: '100% del capitale su singolo asset.',
    detail: 'Solo per strategie baseline. Tipico: buy-and-hold di SPY con tutto il capitale.',
  },
  // === Concetti meta ===
  walk_forward: {
    title: 'Walk-forward',
    short: 'Test out-of-sample rolling.',
    detail: 'Si trova i parametri su una finestra storica, si testano sulla finestra successiva, si avanza. Mostra se la strategia è robusta o curve-fitted.',
  },
  curve_fitting: {
    title: 'Curve Fitting',
    short: 'Overfitting sui dati storici.',
    detail: 'Si trovano parametri che funzionano benissimo sul passato ma falliscono sul futuro. Sintomi: ≥ 4 hyperparam, win rate >70%, Sharpe >3, troppi trade.',
  },
  regime: {
    title: 'Regime di mercato',
    short: 'Fase strutturale del mercato.',
    detail: 'bull-stable, bull-late, range, correction, bear-vol, recovery. Una strategia può avere edge in alcuni regimi e non in altri.',
  },
  look_ahead: {
    title: 'Look-ahead bias',
    short: 'Usare dati del futuro per decidere nel passato.',
    detail: 'Bug subdolo: la strategia "vede" il prezzo di domani per piazzare l\'entry di oggi. Quant Lab applica SHIFT +1 mandatorio per neutralizzarlo.',
  },
};

/** Normalizza una key per matching (lowercase, underscore al posto di spazi). */
export const normalizeTerm = (term) =>
  (term || '').toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/** Lookup principale. Restituisce null se non trovato. */
export const lookup = (term) => GLOSSARY[normalizeTerm(term)] || null;
